import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { HydrationOptionsInterface, Template, TemplateInterface, TemplatesService } from '@vendasta/templates';
import {
  ExtractedValue,
  PostType,
  PostTypeInterface,
  TheLoopService,
  UpdatePostTypeRequestOperationInterface,
} from '@vendasta/the-loop';
import { BehaviorSubject, Observable, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

export const newChannelOption = '+ New Channel';
export const accountGroupIdCommonResourceId = 'account-group-id';
export const matIconIconFormat = 'matIcon';
export const iconPathIconFormat = 'iconPath';

@Component({
  selector: 'app-loop-post-type-editor',
  styleUrls: ['./post-type-editor.component.scss'],
  templateUrl: './post-type-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostTypeEditorComponent implements OnInit {
  public readonly typeId$: Observable<string> = this.route.params.pipe(map((r) => r.typeId));
  public readonly eventId$: Observable<string> = this.route.queryParams.pipe(map((r) => r.eventId));

  templatesLoaded$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  templatesLoaded$: Observable<boolean> = this.templatesLoaded$$.asObservable();

  type$: Observable<PostTypeInterface>;
  form: UntypedFormGroup;

  constructor(
    private route: ActivatedRoute,
    private loopService: TheLoopService,
    private templatesService: TemplatesService,
    private alertService: SnackbarService,
    private fb: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.type$ = combineLatest([this.typeId$, this.eventId$]).pipe(
      switchMap(([typeId, eventId]) => {
        if (!typeId) {
          // creating a new post type, includes some common default values
          return of(
            new PostType({
              eventTypeId: eventId,
              partnerIdPath: 'partner_id',
              marketIdPath: 'market_id',
              commonResources: [{ id: accountGroupIdCommonResourceId, valuePath: 'account_group_id' }],
              iconResource: {
                defaultIcon: {
                  matIcon: 'all_inclusive',
                },
                customTypes: [],
              },
            }),
          );
        }
        // editing an existing post type
        this.form.get('templates').get('templateId').disable();
        return this.loopService.getPostType(typeId);
      }),
      tap((postType) => this.setInitialFormValuesFromPostType(postType)),
      take(1),
      shareReplay(),
    );
  }

  initForm(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      eventTypeId: new UntypedFormControl({ value: '', disabled: true }),
      channel: ['', Validators.required],
      newChannel: [''],
      postIdTemplate: ['', Validators.required],
      partnerIdPath: ['', Validators.required],
      marketIdPath: [''],
      accountGroupIdPath: [''],
      userIdPath: [''],
      iconResource: this.fb.group({
        resourcePath: ['', Validators.required],
        defaultIcon: this.fb.group({
          format: [matIconIconFormat],
          value: [''],
        }),
        customTypes: this.fb.array([]),
      }),
      filters: this.fb.array([]),
      templates: this.fb.group({
        templateId: ['', Validators.required],
        hydrators: this.fb.group({
          accountGroups: this.fb.group({
            checked: [''],
            params: this.fb.group({
              accountGroupIds: [{ value: '', disabled: true }, Validators.required],
              inflatedPropertyName: [{ value: '', disabled: true }, Validators.required],
            }),
          }),
          apps: this.fb.group({
            checked: [''],
            params: this.fb.group({
              appKeys: [{ value: '', disabled: true }, Validators.required],
              partnerId: [{ value: '', disabled: true }, Validators.required],
              marketId: [{ value: '', disabled: true }],
              inflatedPropertyName: [{ value: '', disabled: true }, Validators.required],
            }),
          }),
          packages: this.fb.group({
            checked: [''],
            params: this.fb.group({
              packageIds: [{ value: '', disabled: true }, Validators.required],
              inflatedPropertyName: [{ value: '', disabled: true }, Validators.required],
            }),
          }),
          branding: this.fb.group({
            checked: [''],
            params: this.fb.group({
              partnerId: [{ value: '', disabled: true }, Validators.required],
              marketId: [{ value: '', disabled: true }],
              inflatedPropertyName: [{ value: '', disabled: true }, Validators.required],
            }),
          }),
          users: this.fb.group({
            checked: [''],
            params: this.fb.group({
              userIds: [{ value: '', disabled: true }, Validators.required],
              inflatedPropertyName: [{ value: '', disabled: true }, Validators.required],
            }),
          }),
          salesActivities: this.fb.group({
            checked: [''],
            params: this.fb.group({
              salesActivityKeys: [{ value: '', disabled: true }, Validators.required],
              inflatedPropertyName: [{ value: '', disabled: true }, Validators.required],
            }),
          }),
          partnerConfig: this.fb.group({
            checked: [''],
            params: this.fb.group({
              partnerId: [{ value: '', disabled: true }, Validators.required],
              marketId: [{ value: '', disabled: true }],
              inflatedPropertyName: [{ value: '', disabled: true }, Validators.required],
            }),
          }),
          task: this.fb.group({
            checked: [''],
            params: this.fb.group({
              taskKeys: [{ value: '', disabled: true }, Validators.required],
              inflatedPropertyName: [{ value: '', disabled: true }, Validators.required],
            }),
          }),
        }),
        fullHtml: [''],
        styles: [''],
        compactHTML: [''],
      }),
    });
    // Form should not be ready until templates have loaded
    this.form.get('templates').markAsUntouched();
  }

  setTemplatesLoaded(): void {
    this.templatesLoaded$$.next(true);
  }

  setInitialFormValuesFromPostType(postType: PostTypeInterface): void {
    let defaultIcon = {
      format: iconPathIconFormat,
      value: postType.iconResource.defaultIcon.iconPath,
    };
    if (postType.iconResource.defaultIcon.matIcon) {
      defaultIcon = {
        format: matIconIconFormat,
        value: postType.iconResource.defaultIcon.matIcon,
      };
    }

    const accountGroupIdResource = postType.commonResources.find((r) => r.id === accountGroupIdCommonResourceId);

    this.form.patchValue({
      name: postType.name,
      eventTypeId: postType.eventTypeId,
      channel: postType.channel,
      postIdTemplate: postType.postIdTemplate,
      partnerIdPath: postType.partnerIdPath,
      marketIdPath: postType.marketIdPath,
      accountGroupIdPath: accountGroupIdResource ? accountGroupIdResource.valuePath : '',
      userIdPath: postType.userIdPath,
      iconResource: {
        resourcePath: postType.iconResource.resourcePath,
        defaultIcon: defaultIcon,
      },
      templates: {
        templateId: postType.templateId,
      },
    });

    const customTypes = this.form.get('iconResource').get('customTypes') as UntypedFormArray;
    (postType.iconResource.customTypes || []).forEach((c) => {
      let icon = this.fb.group({
        format: [iconPathIconFormat],
        value: [c.icon.iconPath, Validators.required],
      });
      if (c.icon.matIcon) {
        icon = this.fb.group({
          format: [matIconIconFormat],
          value: [c.icon.matIcon, Validators.required],
        });
      }
      customTypes.push(
        this.fb.group({
          type: [c.type],
          icon: icon,
        }),
      );
    });

    const filters = this.form.get('filters') as UntypedFormArray;
    (postType.filters || []).forEach((f) => {
      const filter = this.fb.group({
        label: [f.label, Validators.required],
        value: this.fb.group({
          id: [f.value.id, Validators.required],
          valuePath: [f.value.valuePath, Validators.required],
        }),
        options: new UntypedFormArray(
          f.options.map((o) => {
            return this.fb.group({
              id: [o.id, Validators.required],
              value: [o.value, Validators.required],
            });
          }),
        ),
      });
      filters.push(filter);
    });
  }

  save(): void {
    if (!this.form.valid) {
      this.alertService.openErrorSnack('Form is invalid');
      return;
    }
    let channel = this.form.get('channel').value;
    if (channel === newChannelOption) {
      channel = this.form.get('newChannel').value;
    }

    const iconResource = this.form.get('iconResource');
    const defaultIcon = {};
    const defaultValue = iconResource.get('defaultIcon').get('value').value;
    if (iconResource.get('defaultIcon').get('format').value === matIconIconFormat) {
      defaultIcon[matIconIconFormat] = defaultValue;
    }
    if (iconResource.get('defaultIcon').get('format').value === iconPathIconFormat) {
      defaultIcon[iconPathIconFormat] = defaultValue;
    }

    const customTypes = iconResource.get('customTypes').value.map((c) => {
      const icon = {};
      const value = c.icon.value;
      if (c.icon.format === matIconIconFormat) {
        icon[matIconIconFormat] = value;
      } else if (c.icon.format === iconPathIconFormat) {
        icon[iconPathIconFormat] = value;
      }
      return {
        type: c.type,
        icon: icon,
      };
    });

    const filters = this.form.get('filters').value.map((c) => {
      const value = {
        id: c.value.id,
        valuePath: c.value.valuePath,
      };
      const options = c.options.map((o) => {
        return {
          id: o.id,
          value: o.value,
        };
      });
      return {
        label: c.label,
        value: value,
        options: options,
      };
    });

    const templateId = this.form.get('templates').get('templateId').value;

    const postType = new PostType({
      name: this.form.get('name').value,
      channel: channel,
      postIdTemplate: this.form.get('postIdTemplate').value,
      partnerIdPath: this.form.get('partnerIdPath').value,
      marketIdPath: this.form.get('marketIdPath').value,
      userIdPath: this.form.get('userIdPath').value,
      templateId: templateId,
      iconResource: {
        resourcePath: iconResource.get('resourcePath').value,
        defaultIcon: defaultIcon,
        customTypes: customTypes,
      },
      filters: filters,
    });

    this.type$
      .pipe(
        tap((type) => {
          postType.eventTypeId = type.eventTypeId;
          postType.commonResources = type.commonResources.map((r) => {
            if (r.id === accountGroupIdCommonResourceId) {
              r.valuePath = this.form.get('accountGroupIdPath').value;
            }
            return new ExtractedValue(r);
          });
          const templateIds = [templateId, templateId + '-styles', templateId + '-compact'];
          const hydrationOptions = this.getHydrationOptions();

          if (!type.typeId) {
            // create a new post type

            this.templatesService
              .getMulti('the-loop', templateIds)
              .pipe(
                take(1),
                map((templates) => {
                  templates.forEach((t) => {
                    if (t.errorDescription !== 'Template not found') {
                      // TODO: allow selecting existing templates?
                      const errMsg = 'Template with id ' + templateId + 'already exists.';
                      this.alertService.openErrorSnack(errMsg);
                      throw new Error(errMsg);
                    }
                  });
                }),
                switchMap(() => {
                  const fullTemplate = new Template({
                    templateId: templateIds[0],
                    name: templateIds[0],
                    content: this.form.get('templates').get('fullHtml').value,
                    hydrationOptions: hydrationOptions,
                  });
                  const stylesTemplate = new Template({
                    templateId: templateIds[1],
                    name: templateIds[1],
                    content: this.form.get('templates').get('styles').value,
                  });
                  const compactTemplate = new Template({
                    templateId: templateIds[2],
                    name: templateIds[2],
                    content: this.form.get('templates').get('compactHTML').value,
                    hydrationOptions: hydrationOptions,
                  });

                  const templateCreateCalls$ = [fullTemplate, stylesTemplate, compactTemplate].map((t) =>
                    this.templatesService.create('the-loop', t),
                  );
                  return forkJoin(templateCreateCalls$);
                }),
                catchError((err: HttpErrorResponse) => {
                  this.alertService.openErrorSnack('Error creating templates.');
                  throw err;
                }),
                switchMap(() => this.loopService.createPostType(postType)),
                catchError((err: HttpErrorResponse) => {
                  this.alertService.openErrorSnack('Error creating post type.');
                  throw err;
                }),
                tap(() => this.alertService.openSuccessSnack('Post type created.')),
              )
              .subscribe();
          } else {
            // update existing post type

            const operations: UpdatePostTypeRequestOperationInterface[] = [
              { name: postType.name },
              { channel: postType.channel },
              { postIdTemplate: postType.postIdTemplate },
              { partnerIdPath: postType.partnerIdPath },
              { marketIdPath: postType.marketIdPath },
              { userIdPath: postType.userIdPath },
              { iconResource: postType.iconResource },
              { commonResources: { commonResources: postType.commonResources } },
              { filters: { filters: postType.filters } },
            ];
            this.templatesService
              .getMulti('the-loop', templateIds)
              .pipe(
                take(1),
                map((templates) => {
                  // TODO handle templates[].error

                  const fullTemplate: TemplateInterface = templates[0].template;
                  fullTemplate.content = this.form.get('templates').get('fullHtml').value;
                  fullTemplate.hydrationOptions = hydrationOptions;

                  const styles: TemplateInterface = templates[1].template;
                  styles.content = this.form.get('templates').get('styles').value;

                  const compactTemplate: TemplateInterface = templates[2].template;
                  compactTemplate.content = this.form.get('templates').get('compactHTML').value;
                  compactTemplate.hydrationOptions = hydrationOptions;

                  return [fullTemplate, styles, compactTemplate];
                }),
                switchMap((templates) => {
                  const results$ = templates.map((t) => this.templatesService.update('the-loop', t));
                  return forkJoin(results$);
                }),
                catchError((err: HttpErrorResponse) => {
                  this.alertService.openErrorSnack('Error updating templates. Nothing will be updated.');
                  throw err;
                }),
                switchMap(() => this.loopService.updatePostType(type.typeId, operations)),
                catchError((err: HttpErrorResponse) => {
                  this.alertService.openErrorSnack('Error updating post type. Only templates have been updated.');
                  throw err;
                }),
                tap(() => this.alertService.openSuccessSnack('Post type updated.')),
              )
              .subscribe();
          }
        }),
        take(1),
      )
      .subscribe();
  }

  getHydrationOptions(): HydrationOptionsInterface {
    const result: HydrationOptionsInterface = {};
    const hydrators = this.form.get('templates').get('hydrators') as UntypedFormGroup;

    const accountGroups = hydrators.get('accountGroups');
    if (accountGroups.get('checked').value) {
      result.accountGroups = {
        accountGroupIds: accountGroups.get('params').get('accountGroupIds').value,
        inflatedPropertyName: accountGroups.get('params').get('inflatedPropertyName').value,
      };
    }
    const apps = hydrators.get('apps');
    if (apps.get('checked').value) {
      result.apps = {
        appKeys: apps.get('params').get('appKeys').value,
        partnerId: apps.get('params').get('partnerId').value,
        marketId: apps.get('params').get('marketId').value,
        inflatedPropertyName: apps.get('params').get('inflatedPropertyName').value,
      };
    }
    const packages = hydrators.get('packages');
    if (packages.get('checked').value) {
      result.packages = {
        packageIds: packages.get('params').get('packageIds').value,
        inflatedPropertyName: packages.get('params').get('inflatedPropertyName').value,
      };
    }
    const branding = hydrators.get('branding');
    if (branding.get('checked').value) {
      result.branding = {
        partnerId: branding.get('params').get('partnerId').value,
        marketId: branding.get('params').get('marketId').value,
        inflatedPropertyName: branding.get('params').get('inflatedPropertyName').value,
      };
    }
    const users = hydrators.get('users');
    if (users.get('checked').value) {
      result.users = {
        userIds: users.get('params').get('userIds').value,
        inflatedPropertyName: users.get('params').get('inflatedPropertyName').value,
      };
    }
    const salesActivities = hydrators.get('salesActivities');
    if (salesActivities.get('checked').value) {
      result.salesActivities = {
        salesActivityKeys: salesActivities.get('params').get('salesActivityKeys').value,
        inflatedPropertyName: salesActivities.get('params').get('inflatedPropertyName').value,
      };
    }
    const partnerConfig = hydrators.get('partnerConfig');
    if (partnerConfig.get('checked').value) {
      result.partnerConfig = {
        partnerId: partnerConfig.get('params').get('partnerId').value,
        marketId: partnerConfig.get('params').get('marketId').value,
        inflatedPropertyName: partnerConfig.get('params').get('inflatedPropertyName').value,
      };
    }
    const task = hydrators.get('task');
    if (task.get('checked').value) {
      result.task = {
        taskKeys: task.get('params').get('taskKeys').value,
        inflatedPropertyName: task.get('params').get('inflatedPropertyName').value,
      };
    }
    return result;
  }
}
